import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { SECTORES } from "../directory/directoryFilters";
import { DEFAULT_STAGES, parseStages, plain } from "../pipeline/OfferForm";
import { useAllOffers } from "../useOffers";
import {
  buildFitRule,
  EMPTY_FIT_CLAUSE,
  hasFitCondition,
  type FitClauseDraft,
} from "./offerWizardRule";
import { buildCallScriptMarkdown } from "./offerWizardPublish";

// Bloque N-mínimo, sesiones 3-5 de 5 (docs/PLAN_BLOQUE_N.md): los 6 pasos +
// "Publicar" (sesión 5). Hasta que se toca "Publicar" nada se escribe en la
// base de datos, solo el borrador en este navegador (incluido lo que
// "Generar con IA" arma en el paso 6 — Ian siempre revisa antes).
// "Publicar" crea la oferta, su regla de encaje y (si hay texto) el guion y
// las plantillas de primer contacto — "pendiente de validar", como ya
// funciona hoy — y llama apply_offer_fit_rule() (RPC) para calcular fits y
// termómetro SOLO de las empresas que encajan, nunca las 2.5 M de golpe.
// ⚠️ apply_offer_fit_rule() vive en supabase/schemas/17_raftel_bloque_n_publicar.sql,
// escrita pero SIN APLICAR a producción — "Publicar" fallará con un aviso
// normal (RPC inexistente) hasta que esa migración se apruebe y se aplique.

const STEPS = [
  "Identidad",
  "Propuesta de valor",
  "Cliente ideal",
  "Oferta comercial",
  "Etapas",
  "Guion y plantillas",
] as const;

const PRICE_MODELS = [
  ["suscripcion", "Suscripción"],
  ["unico", "Pago único"],
  ["comision", "Comisión"],
  ["cotizacion", "Cotización"],
] as const;

const DRAFT_KEY = "raftel_offer_wizard_draft_v1";

type Draft = {
  name: string;
  key: string;
  color: string;
  priority: number;
  valueProblem: string;
  valueFor: string;
  valueResult: string;
  valueDiff: string; // una por línea
  fitClause: FitClauseDraft;
  excluirSectorPublico: boolean;
  priceModel: (typeof PRICE_MODELS)[number][0];
  priceAmount: string;
  priceIva: boolean;
  priceTicket: string;
  stages: string;
  guionApertura: string;
  guionPreguntas: string; // una por línea
  guionCierre: string;
  whatsapp: string;
  correoAsunto: string;
  correoCuerpo: string;
};

const EMPTY_DRAFT: Draft = {
  name: "",
  key: "",
  color: "#0EA5A4",
  priority: 2,
  valueProblem: "",
  valueFor: "",
  valueResult: "",
  valueDiff: "",
  fitClause: EMPTY_FIT_CLAUSE,
  excluirSectorPublico: false,
  priceModel: "suscripcion",
  priceAmount: "",
  priceIva: false,
  priceTicket: "",
  stages: DEFAULT_STAGES,
  guionApertura: "",
  guionPreguntas: "",
  guionCierre: "",
  whatsapp: "",
  correoAsunto: "",
  correoCuerpo: "",
};

const loadDraft = (): Draft => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw);
    return {
      ...EMPTY_DRAFT,
      ...parsed,
      fitClause: { ...EMPTY_FIT_CLAUSE, ...parsed.fitClause },
    };
  } catch {
    return EMPTY_DRAFT;
  }
};

type PreviewResult = { total: number; queretaro: number; ejemplos: unknown[] };

const StepBar = ({ step }: { step: number }) => (
  <ol className="flex flex-wrap gap-2 text-xs font-semibold">
    {STEPS.map((label, i) => (
      <li
        key={label}
        className={cn(
          "flex items-center gap-1.5 rounded-full border px-2.5 py-1",
          i === step
            ? "border-primary bg-accent text-accent-foreground"
            : i < step
              ? "border-transparent bg-secondary text-muted-foreground"
              : "border-dashed text-muted-foreground",
        )}
      >
        <span className="font-mono">{i + 1}</span>
        {label}
      </li>
    ))}
  </ol>
);

const field = "grid gap-1 text-sm font-semibold";

/** Asistente para dar de alta un producto — PRD Bloque N. Abre desde Ajustes. */
export const OfferWizard = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const { data: allOffers } = useAllOffers();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setDraft(loadDraft());
      setStep(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [draft, open]);

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const setClause = (patch: Partial<FitClauseDraft>) =>
    set({ fitClause: { ...draft.fitClause, ...patch } });

  const rule = buildFitRule(draft.fitClause, draft.excluirSectorPublico);
  const ruleHasCondition = hasFitCondition(draft.fitClause);
  const [debouncedRule, setDebouncedRule] = useState(rule);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedRule(rule), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rule)]);

  const { data: preview, isFetching: loadingPreview } = useQuery({
    queryKey: ["offer_fit_preview", debouncedRule],
    enabled: open && step === 2 && ruleHasCondition,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "offer_fit_preview",
        { rule: debouncedRule },
      );
      if (error) throw error;
      return data?.[0] as PreviewResult | undefined;
    },
  });

  const startOver = () => {
    localStorage.removeItem(DRAFT_KEY);
    setDraft(EMPTY_DRAFT);
    setStep(0);
  };

  const canAdvance =
    step === 0 ? draft.name.trim().length > 0 && draft.key.length >= 2 : true;

  const [generating, setGenerating] = useState(false);
  const notify = useNotify();
  const generateWithAI = async () => {
    setGenerating(true);
    const { data, error } = await getSupabaseClient().functions.invoke("ai", {
      body: {
        action: "offer_draft_generate",
        draft: {
          name: draft.name,
          valueProblem: draft.valueProblem,
          valueFor: draft.valueFor,
          valueResult: draft.valueResult,
          valueDiff: draft.valueDiff
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
        },
      },
    });
    setGenerating(false);
    if (error || !data?.draft) {
      notify(
        "La IA no pudo generar el borrador (sin cupo o el motor no respondió). Escribe el guion y las plantillas a mano.",
        { type: "warning" },
      );
      return;
    }
    const d = data.draft as {
      guion: { apertura: string; preguntas: string[]; cierre: string };
      whatsapp: string;
      correo: { asunto: string; cuerpo: string };
    };
    set({
      guionApertura: d.guion.apertura,
      guionPreguntas: d.guion.preguntas.join("\n"),
      guionCierre: d.guion.cierre,
      whatsapp: d.whatsapp,
      correoAsunto: d.correo.asunto,
      correoCuerpo: d.correo.cuerpo,
    });
    notify("Borrador generado — revísalo antes de aprobarlo.", {
      type: "success",
    });
  };

  const [publishing, setPublishing] = useState(false);
  const publish = async () => {
    if (allOffers?.some((o) => o.key === draft.key))
      return notify("Esa clave ya la usa otro tablero", { type: "error" });
    const { list, won, lost } = parseStages(draft.stages);
    if (list.length < 2)
      return notify("Agrega al menos dos etapas", { type: "error" });

    setPublishing(true);
    const sb = getSupabaseClient();
    let slug = plain(draft.name) || "oferta";
    while (allOffers?.some((o) => o.slug === slug)) slug += "2";

    const priceNotes = [
      draft.priceIva ? "Precio incluye IVA." : "Precio sin IVA.",
      draft.priceTicket.trim()
        ? `Ticket promedio esperado: $${draft.priceTicket} MXN.`
        : "",
    ]
      .filter(Boolean)
      .join(" ");

    const { data: created, error: offerError } = await sb
      .from("offers")
      .insert({
        name: draft.name.trim(),
        slug,
        key: draft.key,
        color: draft.color,
        priority: draft.priority,
        stages: list,
        won_stage: won,
        lost_stage: lost,
        value_prop_problem: draft.valueProblem.trim() || null,
        value_prop_for: draft.valueFor.trim() || null,
        value_prop_result: draft.valueResult.trim() || null,
        value_prop_diff: draft.valueDiff
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean),
        price_model: draft.priceModel,
        price_amount: draft.priceAmount.trim()
          ? Number(draft.priceAmount)
          : null,
        price_notes: priceNotes || null,
      })
      .select("id")
      .single();
    if (offerError || !created) {
      setPublishing(false);
      return notify(offerError?.message ?? "No se pudo crear la oferta", {
        type: "error",
      });
    }

    const { error: ruleError } = await sb
      .from("offer_fit_rules")
      .insert({ offer_id: created.id, rule });
    if (ruleError) {
      setPublishing(false);
      return notify(
        `Oferta creada, pero la regla de encaje falló: ${ruleError.message}. Revísalo en Ajustes.`,
        { type: "warning" },
      );
    }

    const templates = [
      draft.guionApertura.trim() || draft.guionCierre.trim()
        ? {
            offer_id: created.id,
            channel: "llamada",
            step_key: "d1_llamada",
            name: "Guion inicial (asistente)",
            body: buildCallScriptMarkdown(
              draft.guionApertura,
              draft.guionPreguntas
                .split("\n")
                .map((s) => s.trim())
                .filter(Boolean),
              draft.guionCierre,
            ),
            origin: "motor",
            pending_validation: true,
          }
        : null,
      draft.whatsapp.trim()
        ? {
            offer_id: created.id,
            channel: "whatsapp",
            step_key: "d1_whatsapp",
            name: "Primer contacto WhatsApp (asistente)",
            body: draft.whatsapp.trim(),
            origin: "motor",
            pending_validation: true,
          }
        : null,
      draft.correoCuerpo.trim()
        ? {
            offer_id: created.id,
            channel: "email",
            step_key: "d3_correo",
            name: "Primer contacto correo (asistente)",
            subject: draft.correoAsunto.trim() || null,
            body: draft.correoCuerpo.trim(),
            origin: "motor",
            pending_validation: true,
          }
        : null,
    ].filter((t): t is NonNullable<typeof t> => t !== null);
    if (templates.length) {
      const { error: tplError } = await sb.from("templates").insert(templates);
      if (tplError)
        notify(
          `Oferta creada, pero el guion/plantillas fallaron: ${tplError.message}. Puedes agregarlos luego en Ajustes.`,
          { type: "warning" },
        );
    }

    let matched = 0;
    if (ruleHasCondition) {
      const { data: n, error: fitError } = await sb.rpc(
        "apply_offer_fit_rule",
        { p_offer_id: created.id },
      );
      if (fitError)
        notify(
          `Oferta creada, pero el cálculo de encaje falló: ${fitError.message}. Corre "seleccionar apply_offer_fit_rule(${created.id})" a mano.`,
          { type: "warning" },
        );
      else matched = (n as number) ?? 0;
    }

    setPublishing(false);
    localStorage.removeItem(DRAFT_KEY);
    ["offers", "funnel_by_offer", "deal_board"].forEach((k) =>
      queryClient.invalidateQueries({ queryKey: [k] }),
    );
    notify(
      ruleHasCondition
        ? `${draft.name} publicado — encaja en ${matched.toLocaleString("es-MX")} empresas del Directorio.`
        : `${draft.name} publicado — sin regla de encaje capturada, no aparece todavía en el Directorio.`,
      { type: "success" },
    );
    onClose();
    navigate(`/board/${created.id}`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Asistente: nuevo producto o servicio</DialogTitle>
          <DialogDescription>
            Tu progreso se guarda solo en este navegador — nada se crea en tu
            CRM hasta que toques "Publicar" en el último paso.
          </DialogDescription>
        </DialogHeader>
        <StepBar step={step} />

        {step === 0 && (
          <div className="grid gap-3">
            <label className={field}>
              Nombre del producto o servicio
              <Input
                value={draft.name}
                onChange={(e) => set({ name: e.target.value })}
                placeholder="Ej. Respuestas a reseñas con IA"
                autoFocus
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className={field}>
                Clave (2-4 letras)
                <Input
                  value={draft.key}
                  onChange={(e) =>
                    set({
                      key: e.target.value
                        .toUpperCase()
                        .replace(/[^A-Z]/g, "")
                        .slice(0, 4),
                    })
                  }
                  placeholder="Ej. RESE"
                />
              </label>
              <label className={field}>
                Color
                <input
                  type="color"
                  className="h-10 w-full rounded-lg border bg-card"
                  value={draft.color}
                  onChange={(e) => set({ color: e.target.value })}
                />
              </label>
              <label className={field}>
                Prioridad
                <Input
                  type="number"
                  min={1}
                  value={draft.priority}
                  onChange={(e) =>
                    set({ priority: Number(e.target.value) || 1 })
                  }
                />
              </label>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-3">
            <label className={field}>
              ¿Qué problema resuelve?
              <Textarea
                value={draft.valueProblem}
                onChange={(e) => set({ valueProblem: e.target.value })}
                rows={2}
              />
            </label>
            <label className={field}>
              ¿Para quién es?
              <Textarea
                value={draft.valueFor}
                onChange={(e) => set({ valueFor: e.target.value })}
                rows={2}
              />
            </label>
            <label className={field}>
              ¿Qué resultado promete?
              <Textarea
                value={draft.valueResult}
                onChange={(e) => set({ valueResult: e.target.value })}
                rows={2}
              />
            </label>
            <label className={field}>
              Diferenciadores (uno por línea)
              <Textarea
                value={draft.valueDiff}
                onChange={(e) => set({ valueDiff: e.target.value })}
                rows={3}
              />
            </label>
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-3">
            <div className={field}>
              Sectores
              <div className="flex flex-wrap gap-1.5">
                {SECTORES.map((s) => {
                  const on = draft.fitClause.sectores.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() =>
                        setClause({
                          sectores: on
                            ? draft.fitClause.sectores.filter((x) => x !== s)
                            : [...draft.fitClause.sectores, s],
                        })
                      }
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                        on
                          ? "border-primary bg-accent text-accent-foreground"
                          : "bg-card",
                      )}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className={field}>
                SCIAN a incluir (separados por coma)
                <Input
                  value={draft.fitClause.scianIncluir}
                  onChange={(e) => setClause({ scianIncluir: e.target.value })}
                  placeholder="7225%, 7224%"
                />
              </label>
              <label className={field}>
                SCIAN a excluir
                <Input
                  value={draft.fitClause.scianExcluir}
                  onChange={(e) => setClause({ scianExcluir: e.target.value })}
                  placeholder="4684%"
                />
              </label>
              <label className={field}>
                Empleados mínimos
                <Input
                  type="number"
                  min={0}
                  value={draft.fitClause.empMin}
                  onChange={(e) => setClause({ empMin: e.target.value })}
                />
              </label>
              <label className="flex items-end gap-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={draft.excluirSectorPublico}
                  onChange={(e) =>
                    set({ excluirSectorPublico: e.target.checked })
                  }
                />
                Excluir sector público
              </label>
              <label className={field}>
                Palabra o expresión a buscar en el nombre (opcional)
                <Input
                  value={draft.fitClause.nombreIncluir}
                  onChange={(e) => setClause({ nombreIncluir: e.target.value })}
                  placeholder="extintor|matafuego"
                />
              </label>
              <label className={field}>
                Palabra o expresión a excluir del nombre (opcional)
                <Input
                  value={draft.fitClause.nombreExcluir}
                  onChange={(e) => setClause({ nombreExcluir: e.target.value })}
                  placeholder="distribuidora"
                />
              </label>
            </div>
            <div className="rounded-lg bg-secondary p-3 text-sm">
              {!ruleHasCondition ? (
                <p className="text-muted-foreground">
                  Agrega al menos una condición para ver cuántas empresas
                  encajarían.
                </p>
              ) : loadingPreview && !preview ? (
                <p className="text-muted-foreground">Calculando…</p>
              ) : preview ? (
                <p>
                  Encajan{" "}
                  <strong>{preview.total.toLocaleString("es-MX")}</strong>{" "}
                  empresas en el país, <strong>{preview.queretaro}</strong> en
                  Querétaro.
                </p>
              ) : null}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="grid gap-3 sm:grid-cols-2">
            <label className={field}>
              Modelo de precio
              <select
                className="h-10 w-full rounded-lg border bg-card px-3 text-sm"
                value={draft.priceModel}
                onChange={(e) =>
                  set({
                    priceModel: e.target.value as Draft["priceModel"],
                  })
                }
              >
                {PRICE_MODELS.map(([v, label]) => (
                  <option key={v} value={v}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label className={field}>
              Precio en MXN (sin IVA)
              <Input
                type="number"
                min={0}
                value={draft.priceAmount}
                onChange={(e) => set({ priceAmount: e.target.value })}
              />
            </label>
            <label className="flex items-end gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={draft.priceIva}
                onChange={(e) => set({ priceIva: e.target.checked })}
              />
              Incluye IVA
            </label>
            <label className={field}>
              Ticket promedio esperado (MXN)
              <Input
                type="number"
                min={0}
                value={draft.priceTicket}
                onChange={(e) => set({ priceTicket: e.target.value })}
              />
            </label>
          </div>
        )}

        {step === 4 && (
          <div className="grid gap-3">
            <label className={field}>
              Etapas, en orden y separadas por coma
              <Input
                value={draft.stages}
                onChange={(e) => set({ stages: e.target.value })}
              />
              <span className="text-xs font-normal text-muted-foreground">
                Termina con Ganado y Perdido para que cuenten tus tasas de
                cierre — mismo formato que "+ Nuevo producto o servicio".
              </span>
            </label>
            {(() => {
              const { list, won, lost } = parseStages(draft.stages);
              return (
                <div className="flex flex-wrap gap-1.5">
                  {list.map((s) => (
                    <span
                      key={s}
                      className={cn(
                        "rounded-full border px-2.5 py-0.5 text-xs font-semibold",
                        s === won
                          ? "border-primary bg-accent text-accent-foreground"
                          : s === lost
                            ? "border-destructive text-destructive"
                            : "bg-card",
                      )}
                    >
                      {s}
                      {s === won ? " · ganado" : s === lost ? " · perdido" : ""}
                    </span>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {step === 5 && (
          <div className="grid gap-3">
            <Button
              variant="outline"
              onClick={generateWithAI}
              disabled={generating || !draft.name.trim()}
              className="self-start"
            >
              {generating ? "Generando…" : "Generar con IA"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Usa la identidad y la propuesta de valor de los pasos 1 y 2 para
              armar un primer borrador. Revísalo siempre antes de continuar —
              nada se guarda ni se envía todavía.
            </p>
            <label className={field}>
              Guion — apertura
              <Textarea
                value={draft.guionApertura}
                onChange={(e) => set({ guionApertura: e.target.value })}
                rows={2}
              />
            </label>
            <label className={field}>
              Guion — preguntas (una por línea)
              <Textarea
                value={draft.guionPreguntas}
                onChange={(e) => set({ guionPreguntas: e.target.value })}
                rows={3}
              />
            </label>
            <label className={field}>
              Guion — cierre
              <Textarea
                value={draft.guionCierre}
                onChange={(e) => set({ guionCierre: e.target.value })}
                rows={2}
              />
            </label>
            <label className={field}>
              WhatsApp de primer contacto
              <Textarea
                value={draft.whatsapp}
                onChange={(e) => set({ whatsapp: e.target.value })}
                rows={2}
              />
            </label>
            <label className={field}>
              Correo — asunto
              <Input
                value={draft.correoAsunto}
                onChange={(e) => set({ correoAsunto: e.target.value })}
              />
            </label>
            <label className={field}>
              Correo — cuerpo
              <Textarea
                value={draft.correoCuerpo}
                onChange={(e) => set({ correoCuerpo: e.target.value })}
                rows={3}
              />
            </label>
            <p className="rounded-lg bg-secondary p-3 text-xs text-muted-foreground">
              Pendiente de validar — al publicar, el guion y las plantillas
              quedan marcados así en Ajustes → Textos por validar hasta que los
              revises.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            className="text-destructive"
            onClick={startOver}
          >
            Empezar de nuevo
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cerrar (el borrador se conserva)
            </Button>
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                Atrás
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button disabled={!canAdvance} onClick={() => setStep(step + 1)}>
                Siguiente
              </Button>
            )}
            {step === STEPS.length - 1 && (
              <Button disabled={publishing} onClick={publish}>
                {publishing ? "Publicando…" : "Publicar"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
